import { VALTYPES, VALTYPE_ANY } from '../../../../data/interfaces';
import { createColorMappingAspect, createColorMappingConfigs } from '../../helpers/colorMappingInfo';
import { createConfigParamTypeRange } from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { Correlogram } from './Correlogram';

export const manifestCorrelogram: TpChartManifest = {
  id: 'Correlogram',
  name: 'Correlogram',
  description: `Creates a scatter plot for each pair of selected numerical properties. Each point represents one row entity in the primary table; its position in each scatter plot is determined by that entity's values for the pair of properties.

Data setup: the correlogram uses a primary table and a linked table describing the plotted numerical properties.
- The primary table is selected by \`dataBinding.dataTableId\`. Its rows are the point entities, and its columns hold the numerical measurements to compare. Group the selected numerical properties together using a shared \`groupId\`.
- Each selected numerical property must also specify \`columnCanonicalTypeId\`, set to the linked table's canonical type, and \`columnItemId\`, set to the item ID of the linked-table entity that describes the measurement (**update required to table manifest**). The \`columnItemId\` is an entity ID, not the numerical property's own \`id\`.
- Set \`dataBinding.columnsGroupId\` to the properties' shared \`groupId\` and \`dataBinding.columnsCanonicalTypeId\` to the linked table's canonical type ID. The chart selects properties matching both values and uses each \`columnItemId\` to match a property to its linked-table row.
- Bind \`columnLabelId\`, \`sortColumnBy\`, and \`hoverLabelColumn\` to properties in the linked table; \`sortColumnBy\` is optional. Point color and hover-label bindings, when used, refer to the primary table's row entities.

Example: compare clinical measurements across study participants.
- The primary participants table has one row per participant, with a unique item ID such as \`participant_01\`. It contains one numeric property per measurement, for example \`age_years\`, \`bmi\`, and \`marker_a\`; each participant's row stores their value for each measurement. Put these properties in one property group, for example \`measurements\`.
- For each measurement property, set \`groupId: measurements\`, \`columnCanonicalTypeId\` to the measurement table's canonical type (for example, \`measurement\`), and \`columnItemId\` to the corresponding measurement's item ID (for example, \`measure_age\`). The measurement property's own ID can be different from \`measure_age\`.
- The linked measurement table has one row per measurement, with item IDs such as \`measure_age\`, \`measure_bmi\`, and \`measure_marker_a\`. It may provide display names, units, or categories such as measurement domain. These rows describe the plotted variables; they are not the scatter-plot points.
- Bind \`columnLabelId\` to the measurement display name and \`hoverLabelColumn\` to descriptive metadata such as units. Configure \`columnsGroupId: measurements\` and \`columnsCanonicalTypeId: measurement\`. The correlogram then plots each pair of measurements across participants, with each participant forming a point.
`,
  showDataMatrix: true,
  dataMatrixDocumentationGroup: 'columns',
  component: Correlogram,
  canvasBased: true,
  groups: [
    { groupId: 'columns', groupName: 'Columns' },
    { groupId: 'pointColor', groupName: 'Point color' },
    { groupId: 'pointLayout', groupName: 'Point layout' },
  ],
  aspects: [
    createColorMappingAspect('color', 'Point color data', 'pointColor', 0.5, 'data points'),
    {
      id: 'hoverLabel',
      name: 'Hover label',
      groupId: 'pointLayout',
      description: 'Text to be displayed when the mouse hovers over a data point.',
      required: false,
      dataType: VALTYPE_ANY,
    },
    {
      id: 'columnLabelId',
      name: 'Column labels',
      groupId: 'columns',
      description: 'Displayed labels to identify the numerical properties used in the plot.',
      required: true,
      onColumnItems: true,
      dataType: [VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'sortColumnBy',
      name: 'Sort columns by',
      groupId: 'columns',
      description: 'Sort order of the numerical properties used in the plot.',
      onColumnItems: true,
      dataType: [VALTYPES.DENDROGRAM, VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'hoverLabelColumn',
      name: 'Hover label (columns)',
      description: 'Text to be displayed when the mouse hovers over a property label.',
      groupId: 'columns',
      onColumnItems: true,
      required: true,
      dataType: VALTYPE_ANY,
    },
  ],
  configParams: [
    {
      id: 'opacity',
      name: 'Opacity',
      description: `Point opacity.
In case a plot contains many overlapping points, lowering the opacity (i.e. increasing transparency)
can help reveal the data structure.`,
      groupId: 'pointLayout',
      paramType: createConfigParamTypeRange(0.1, 1, 0.1, 0.7),
    },
    ...createColorMappingConfigs('color', 'Color', 'pointColor'),
  ],
  examples: [
    {
      id: 'correlogram_1',
    },
  ],
};
